// HPL Vehicle Condition Report — document engine (pdf-lib).
// Captures the vehicle's condition at handover: odometer, fuel, written notes per
// area, and photos. Filed against the vehicle's private documents and referenced by
// the lease so the driver is never charged on return for pre-existing damage
// (clause 12 — "fair wear and tear excepted").

import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { base64ToBytes } from './leaseContract';

const fmtDate = (d) => {
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date)) return '____________';
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
};

// Inspection areas — shared so the lease-signing flow can rebuild a report from its
// stored form (with signatures) identically to how the drawer generated it.
export const CR_AREAS = [
  { key: 'exterior',   short: 'Exterior',   label: 'Exterior — panels, paint, scratches, dents' },
  { key: 'interior',   short: 'Interior',   label: 'Interior — seats, trim, dash, cleanliness' },
  { key: 'tyres',      short: 'Tyres',      label: 'Tyres & wheels' },
  { key: 'mechanical', short: 'Mechanical', label: 'Mechanical / dashboard warnings' },
];
const CR_AREA_SHORT = Object.fromEntries(CR_AREAS.map((a) => [a.key, a.short]));

// Turn a stored condition-report form (photos as base64 or bytes) into the input
// for buildConditionReportPdfBytes. Caller adds signature fields as needed.
export function formToConditionData(form, vehicle) {
  const order = ['exterior', 'interior', 'tyres', 'mechanical', 'general'];
  const photos = [...(form.photos || [])]
    .sort((a, b) => order.indexOf(a.area) - order.indexOf(b.area))
    .map((p) => ({
      bytes: p.bytes ? p.bytes : base64ToBytes(p.base64),
      caption: [CR_AREA_SHORT[p.area] || '', p.caption].filter(Boolean).join(' — '),
    }));
  return {
    vehicle,
    odometer: form.odometer, fuelLevel: form.fuelLevel,
    lesseeName: form.lesseeName, inspectedBy: form.inspectedBy,
    smokeOdour: !!form.smokeOdour, notes: form.extraNotes || '',
    areas: CR_AREAS.map((a) => ({ label: a.label, note: (form.notes || {})[a.key] || '' })),
    photos,
  };
}

export async function buildConditionReportPdfBytes(d) {
  const pdf = await PDFDocument.create();
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);

  const PW = 595.28, PH = 841.89, M = 54, FOOT = 48;
  const colW = PW - M * 2;
  const GOLD = rgb(0.788, 0.659, 0.298);
  const INK = rgb(0.067, 0.067, 0.078);
  const GREY = rgb(0.5, 0.5, 0.5);

  let page = pdf.addPage([PW, PH]);

  // Header band
  const BAND = 92;
  page.drawRectangle({ x: 0, y: PH - BAND, width: PW, height: BAND, color: INK });
  page.drawRectangle({ x: 0, y: PH - BAND - 3, width: PW, height: 3, color: GOLD });
  page.drawText('HOLLYPAUL', { x: M, y: PH - 46, size: 25, font: bold, color: GOLD });
  page.drawText('LEASING PTY LTD', { x: M, y: PH - 64, size: 9, font, color: rgb(0.85, 0.85, 0.9) });
  const hTitle = 'VEHICLE CONDITION REPORT';
  page.drawText(hTitle, { x: PW - M - bold.widthOfTextAtSize(hTitle, 13), y: PH - 44, size: 13, font: bold, color: rgb(1, 1, 1) });
  const hSub = `At handover — ${fmtDate(d.date || new Date())}`;
  page.drawText(hSub, { x: PW - M - font.widthOfTextAtSize(hSub, 8.5), y: PH - 60, size: 8.5, font, color: rgb(0.7, 0.7, 0.78) });

  let y = PH - BAND - 22;
  const ensure = (need) => { if (y - need < FOOT) { page = pdf.addPage([PW, PH]); y = PH - M; } };

  const wrap = (text, f, size, maxW) => {
    const words = String(text == null ? '' : text).split(/\s+/);
    const lines = []; let line = '';
    for (const w of words) {
      const test = line ? `${line} ${w}` : w;
      if (f.widthOfTextAtSize(test, size) > maxW && line) { lines.push(line); line = w; } else line = test;
    }
    if (line) lines.push(line);
    return lines.length ? lines : [''];
  };
  const para = (text, { f = font, size = 9.5, indent = 0, color = rgb(0.12, 0.12, 0.12), gap = 5 } = {}) => {
    for (const ln of wrap(text, f, size, colW - indent)) {
      ensure(size * 1.4);
      page.drawText(ln, { x: M + indent, y: y - size, size, font: f, color });
      y -= size * 1.4;
    }
    y -= gap;
  };
  const heading = (text) => { ensure(26); y -= 8; para(text, { f: bold, size: 11, gap: 3, color: INK }); page.drawRectangle({ x: M, y: y + 1, width: 38, height: 2, color: GOLD }); y -= 8; };
  const kv = (label, value) => {
    ensure(15);
    page.drawText(`${label}:`, { x: M, y: y - 9.5, size: 9.5, font: bold, color: rgb(0.12, 0.12, 0.12) });
    for (const ln of wrap(value, font, 9.5, colW - 160)) {
      ensure(13);
      page.drawText(ln, { x: M + 160, y: y - 9.5, size: 9.5, font, color: rgb(0.12, 0.12, 0.12) });
      y -= 13;
    }
    y -= 2;
  };

  const veh = d.vehicle || {};
  const vehDesc = [veh.year, veh.make, veh.model].filter(Boolean).join(' ') || '—';

  kv('Vehicle', `${vehDesc}`);
  kv('Registration', veh.rego || '—');
  kv('Odometer at handover', d.odometer ? `${Number(d.odometer).toLocaleString('en-AU')} km` : '—');
  if (d.fuelLevel) kv('Fuel level', d.fuelLevel);
  kv('Driver / Lessee', d.lesseeName || '—');
  kv('Inspected by', d.inspectedBy || '—');

  heading('CONDITION AT HANDOVER');
  para('The following pre-existing condition is recorded at handover and is excluded from the end-of-lease damage assessment (fair wear and tear excepted, per clause 12 of the lease).', { color: GREY, size: 8.5, gap: 8 });

  const areas = d.areas || [];
  for (const a of areas) {
    if (!a || !(a.note && a.note.trim())) continue;
    para(a.label, { f: bold, size: 9.5, gap: 2 });
    para(a.note, { indent: 10, gap: 6 });
  }
  if (d.smokeOdour) para('• Interior has a noticeable cigarette-smoke odour (pre-existing, from a previous driver).', { indent: 0, gap: 6 });
  if (d.notes && d.notes.trim()) { para('Additional notes', { f: bold, size: 9.5, gap: 2 }); para(d.notes, { indent: 10, gap: 6 }); }
  if (!areas.some(a => a && a.note && a.note.trim()) && !d.smokeOdour && !(d.notes && d.notes.trim())) {
    para('No pre-existing damage noted.', { indent: 0, gap: 6 });
  }

  // Photos
  const photos = (d.photos || []).filter(p => p && p.bytes);
  if (photos.length) {
    heading(`PHOTOS (${photos.length})`);
    for (const p of photos) {
      let img;
      try { img = await pdf.embedJpg(p.bytes); }
      catch { try { img = await pdf.embedPng(p.bytes); } catch { img = null; } }
      if (!img) continue;
      const ratio = img.width / img.height || 1;
      let w = colW, h = w / ratio;
      if (h > 300) { h = 300; w = h * ratio; }
      ensure(h + 22);
      page.drawImage(img, { x: M + (colW - w) / 2, y: y - h, width: w, height: h });
      y -= h + 4;
      if (p.caption) para(p.caption, { size: 8.5, color: GREY, gap: 8 });
      else y -= 8;
    }
  }

  // Acknowledgement + signatures
  heading('ACKNOWLEDGEMENT');
  para('The Lessee and the Lessor agree that this report fairly records the condition of the vehicle at the time of handover. Items noted or photographed above are pre-existing and will not be charged to the Lessee on return.', { gap: 10 });

  const sig = async (label, value, image) => {
    ensure(30); y -= 8;
    if (image && String(image).startsWith('data:image')) {
      try {
        const png = await pdf.embedPng(image);
        const h = 26, w = (png.width / png.height) * h;
        page.drawImage(png, { x: M + 114, y: y - 24, width: Math.min(w, 190), height: h });
      } catch { /* ignore bad signature image */ }
    }
    page.drawText(`${label}:`, { x: M, y: y - 9, size: 9.5, font, color: rgb(0.12, 0.12, 0.12) });
    page.drawLine({ start: { x: M + 110, y: y - 12 }, end: { x: M + 330, y: y - 12 }, thickness: 0.6, color: rgb(0.5, 0.5, 0.5) });
    if (value && !String(value).startsWith('data:image')) page.drawText(String(value), { x: M + 114, y: y - 9, size: 9.5, font, color: rgb(0.12, 0.12, 0.12) });
    y -= 20;
  };
  para('Lessee (driver):', { f: bold, size: 9.5, gap: 2 });
  await sig('Name', d.lesseeName || null);
  await sig('Signature', null, d.lesseeSignature);
  await sig('Date', d.lesseeSignedDate ? fmtDate(d.lesseeSignedDate) : null);
  y -= 4;
  para('For Hollypaul Leasing Pty Ltd:', { f: bold, size: 9.5, gap: 2 });
  await sig('Name', d.lessorSignatory || d.inspectedBy || null);
  await sig('Signature', null, d.lessorSignature);
  await sig('Date', d.lessorSignedDate ? fmtDate(d.lessorSignedDate) : null);

  // Footers
  const pages = pdf.getPages();
  const total = pages.length;
  pages.forEach((pg, i) => {
    pg.drawLine({ start: { x: M, y: 38 }, end: { x: PW - M, y: 38 }, thickness: 0.5, color: rgb(0.8, 0.8, 0.8) });
    pg.drawText('HOLLYPAUL LEASING PTY LTD · Vehicle Condition Report', { x: M, y: 27, size: 7, font, color: GREY });
    const pn = `Page ${i + 1} of ${total}`;
    pg.drawText(pn, { x: PW - M - font.widthOfTextAtSize(pn, 7), y: 27, size: 7, font, color: GREY });
  });

  return pdf.save();
}
